import { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { useBrandReport, useBrandTrend } from '../../../../hooks/useVisibility';
import { useFilters, shiftDate } from '../filters';
import { pct, score, pos, deltaProps, brandColor } from '../format';
import { Table, THead, Th, Td } from '../../../../components/ui/Table';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

export function BrandLeaderboard({ limit = 7, title = 'Top brands', action }: { limit?: number; title?: string; action?: React.ReactNode }) {
  const { startDate, endDate } = useFilters();
  const { data, isLoading } = useBrandReport(startDate, endDate, { order_by: 'visibility' });

  const windowDays = Math.max(
    1,
    Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000) + 1,
  );
  const prevEnd = shiftDate(startDate, -1);
  const prevStart = shiftDate(prevEnd, -(windowDays - 1));
  const { data: prevData } = useBrandReport(prevStart, prevEnd, { order_by: 'visibility' });

  const sparkStart = shiftDate(endDate, -6);
  const { data: trend } = useBrandTrend(sparkStart, endDate);
  const trendByBrand = useMemo(() => {
    const map = new Map<string, { date: string; visibility: number }[]>();
    for (const r of trend?.items ?? []) map.set(r.brandId, r.points);
    return map;
  }, [trend]);

  const prevByBrandId = useMemo(() => {
    if (!prevData) return new Map<string, number | null>();
    const m = new Map<string, number | null>();
    const idIdx = prevData.columns.indexOf('brand_id');
    const visIdx = prevData.columns.indexOf('visibility');
    for (const row of prevData.rows) m.set(row[idIdx] as string, row[visIdx] as number | null);
    return m;
  }, [prevData]);

  return (
    <Card padding="md">
      <CardHeader
        title={title}
        description="Visibility, share of voice and rank vs. competitors"
        {...(action ? { action } : {})}
      />
      {isLoading ? (
        <LoadingState />
      ) : !data || data.rows.length === 0 ? (
        <EmptyState compact title="No brand data." />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <tr>
                <Th className="px-3 w-10">#</Th>
                <Th className="px-3">Brand</Th>
                <Th align="right" className="px-3">Visibility</Th>
                <Th align="right" className="px-3">Δ</Th>
                <Th align="right" className="px-3">7-day</Th>
                <Th align="right" className="px-3">SoV</Th>
                <Th align="right" className="px-3">Sentiment</Th>
                <Th align="right" className="px-3">Position</Th>
              </tr>
            </THead>
            <tbody>
              {data.rows.slice(0, limit).map((row, i) => {
                const idx = {
                  brandId: data.columns.indexOf('brand_id'),
                  brandName: data.columns.indexOf('brand_name'),
                  isOwn: data.columns.indexOf('is_own'),
                  visibility: data.columns.indexOf('visibility'),
                  sov: data.columns.indexOf('share_of_voice'),
                  sentiment: data.columns.indexOf('sentiment'),
                  position: data.columns.indexOf('position'),
                };
                const isOwn = row[idx.isOwn] === 1;
                const brandId = row[idx.brandId] as string;
                const points = trendByBrand.get(brandId) ?? [];
                const vis = row[idx.visibility] as number | null;
                const prevVis = prevByBrandId.get(brandId) ?? null;
                const d = deltaProps(vis, prevVis, { suffix: 'pt' });

                return (
                  <tr key={i} className={isOwn ? 'bg-info-light/40' : 'border-t border-border-light'}>
                    <Td className="px-3 tabular-nums text-text-tertiary">{i + 1}</Td>
                    <Td className="px-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: brandColor(brandId, isOwn) }} />
                        {row[idx.brandName]}
                        {isOwn && <Badge variant="info">You</Badge>}
                      </span>
                    </Td>
                    <Td align="right" className="px-3 tabular-nums">{pct(vis)}</Td>
                    <Td align="right" className="px-3 tabular-nums">
                      {d ? (
                        <span className={d.trend === 'up' ? 'text-success' : d.trend === 'down' ? 'text-error' : 'text-text-tertiary'}>
                          {d.value}
                        </span>
                      ) : <span className="text-text-tertiary">—</span>}
                    </Td>
                    <Td className="px-3" style={{ minWidth: 80 }}>
                      {points.length >= 3 ? (
                        <div style={{ width: 80, height: 24 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={points}>
                              <Line
                                type="linear"
                                dataKey="visibility"
                                stroke={brandColor(brandId, isOwn)}
                                strokeWidth={1.5}
                                dot={false}
                                isAnimationActive={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <span className="text-text-tertiary text-body-xs" title="≥3 days of data needed">—</span>
                      )}
                    </Td>
                    <Td align="right" className="px-3 tabular-nums">{pct(row[idx.sov] as number | null)}</Td>
                    <Td align="right" className="px-3 tabular-nums">{score(row[idx.sentiment] as number | null)}</Td>
                    <Td align="right" className="px-3 tabular-nums">{pos(row[idx.position] as number | null)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}
